/**
 * Centralized Maritime Categories Configuration
 *
 * SINGLE SOURCE OF TRUTH for all marketplace categories across the app.
 * Used by: MarketplaceLanding, MarketplaceSearch, MarketplaceTab, ExperienceDetail,
 *          WidgetCustomizationNew, DistributionTab.
 *
 * BookingFlows is a maritime-focused platform — every category here is on-water /
 * boating related. To add a category: add an entry below with a unique `id` (slug),
 * `name`, `icon`, `description`, and `image`. Every consumer updates automatically.
 *
 * NOTE: the backend public-browse endpoint (routes/marketplace_v2.py `category_meta`)
 * mirrors these slugs for display names/icons — keep the two in sync when editing.
 */

// Stable, known-good maritime/water Unsplash images (reused intentionally to avoid 404s).
const IMG = {
  open_water: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=300&h=200&fit=crop',
  boat: 'https://images.unsplash.com/photo-1648997934392-7213a9ce50b7?w=300&h=200&fit=crop',
  watersport: 'https://images.unsplash.com/photo-1530053969600-caed2596d242?w=300&h=200&fit=crop',
  coast: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=300&h=200&fit=crop',
  sunset: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300&h=200&fit=crop',
};

export const CATEGORIES = [
  { id: 'all', name: 'All', icon: '✨', description: 'Browse all experiences', image: IMG.open_water },
  { id: 'pontoon-rentals', name: 'Pontoon Rentals', icon: '🚤', description: 'Relaxed, group-friendly pontoon boats for a day on the water', image: IMG.boat },
  { id: 'party-boat-rentals', name: 'Party Boat Rentals', icon: '🎉', description: 'Big-group party boats for celebrations and events', image: IMG.boat },
  { id: 'jet-ski-rentals', name: 'Jet Ski & PWC', icon: '🌊', description: 'Jet ski and personal watercraft rentals', image: IMG.watersport },
  { id: 'yacht-charters', name: 'Yacht Charters', icon: '🛥️', description: 'Private luxury yacht charters with crew', image: IMG.boat },
  { id: 'sailing', name: 'Sailing & Sailboats', icon: '⛵', description: 'Sailboat charters, sailing cruises, and catamarans', image: IMG.sunset },
  { id: 'fishing-charters', name: 'Fishing Charters', icon: '🎣', description: 'Inshore, offshore, and lake fishing trips', image: IMG.boat },
  { id: 'boat-tours', name: 'Boat Tours & Cruises', icon: '🚢', description: 'Sightseeing cruises and guided water excursions', image: IMG.boat },
  { id: 'sunset-cruises', name: 'Sunset & Dinner Cruises', icon: '🌅', description: 'Sunset, dinner, and special-occasion cruises', image: IMG.sunset },
  { id: 'scuba-snorkeling', name: 'Scuba & Snorkeling', icon: '🤿', description: 'Dive trips, snorkeling tours, and reef adventures', image: IMG.watersport },
  { id: 'water-sports', name: 'Watersports', icon: '🏄', description: 'Wakeboarding, tubing, water-ski, and parasailing', image: IMG.watersport },
  { id: 'kayak-paddleboard', name: 'Kayak & Paddleboard', icon: '🛶', description: 'Kayak, canoe, and stand-up paddleboard rentals & tours', image: IMG.watersport },
  { id: 'flyboard', name: 'Flyboard & Jetpack', icon: '🚀', description: 'Flyboarding, hoverboarding, and water jetpacks', image: IMG.watersport },
  { id: 'whale-watching', name: 'Whale & Wildlife Watching', icon: '🐬', description: 'Whale, dolphin, and marine wildlife tours', image: IMG.coast },
  { id: 'lessons', name: 'Lessons & Courses', icon: '🎓', description: 'Sailing school, captain’s license, RYA, and on-water lessons', image: IMG.open_water },
  { id: 'overnight-stays', name: 'Overnight Stays', icon: '🛏️', description: 'Houseboats, liveaboards, and waterfront cabins', image: IMG.open_water },
  { id: 'boat-rentals', name: 'Boat Rentals', icon: '⚓', description: 'General and bareboat boat rentals', image: IMG.boat },
];

// Quick filter categories for the search page - show all categories
export const QUICK_FILTER_CATEGORIES = CATEGORIES;

// Get category by ID
export const getCategoryById = (id) => {
  return CATEGORIES.find(cat => cat.id === id) || null;
};

// Get category name by ID
export const getCategoryName = (id) => {
  const category = getCategoryById(id);
  return category ? category.name : id;
};

// Get category icon by ID
export const getCategoryIcon = (id) => {
  const category = getCategoryById(id);
  return category ? category.icon : '📍';
};

// Categories without 'all' for dropdowns/selects
export const CATEGORY_OPTIONS = CATEGORIES.filter(cat => cat.id !== 'all');

// Emoji icons for operator-created categories that aren't in the maritime list
const GENERIC_CATEGORY_ICONS = {
  'adventure': '🧗',
  'nature': '🦜',
  'classes': '🎓',
  'food-drink': '🍽️',
  'food_drink': '🍽️',
  'sightseeing': '📸',
  'cultural': '🏛️',
  'wellness-spa': '💆',
};

/**
 * Resolve display name + icon for ANY category id — including operator-created
 * ones the static list doesn't know about (marketplace pills are data-driven).
 */
export const getCategoryMeta = (id, apiDisplayName = '') => {
  const known = getCategoryById(id);
  if (known) return { name: known.name, icon: known.icon, image: known.image };
  const name = apiDisplayName || String(id || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { name, icon: GENERIC_CATEGORY_ICONS[id] || '✨', image: IMG.coast };
};

export default CATEGORIES;
